import { Directive, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

@Directive({
    selector: 'audio[visualizer]',
    standalone: true
})

export class AudioVisualizerDirective implements AfterViewInit, OnDestroy {

    private audioContext!: AudioContext;
    private analyser!: AnalyserNode;
    private canvas!: HTMLCanvasElement;
    private canvasContext: CanvasRenderingContext2D | null = null;
    private audioElement: HTMLAudioElement;
    private animationId!: number;
    private dataArray!: Uint8Array;

    constructor(
        private _el: ElementRef
    ) {
        this.audioElement = this._el.nativeElement as HTMLAudioElement;
        // Add crossorigin attribute
        this.audioElement.crossOrigin = 'anonymous';
    }

    ngAfterViewInit(): void {
        // Delay setup to ensure DOM is ready
        setTimeout(() => {
            this.setupVisualizer();
        }, 100);
    }

    private setupVisualizer(): void {
        // Create canvas element
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.audioElement.clientWidth || 300;
        this.canvas.height = 100;
        this.canvas.classList.add('audio-visualizer');

        // Style the canvas
        this.canvas.style.width = '100%';
        this.canvas.style.marginTop = '10px';
        this.canvas.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
        this.canvas.style.borderRadius = '5px';

        // Insert canvas after audio element
        if (this.audioElement.parentNode) {
            this.audioElement.parentNode.insertBefore(this.canvas, this.audioElement.nextSibling);
        }

        // Get canvas context
        const context = this.canvas.getContext('2d');
        if (!context) {
            console.error('Failed to get 2D context from canvas');
            return;
        }
        this.canvasContext = context;

        try {
            // Create audio context and analyzer
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;

            // Create buffer
            const bufferLength = this.analyser.frequencyBinCount;
            this.dataArray = new Uint8Array(bufferLength);

            // Connect audio to analyzer
            const source = this.audioContext.createMediaElementSource(this.audioElement);
            source.connect(this.analyser);
            this.analyser.connect(this.audioContext.destination);

            // Start visualization
            this.visualize();

            // Handle browser autoplay restrictions
            this.audioElement.addEventListener('play', () => {
                if (this.audioContext && this.audioContext.state === 'suspended') {
                    this.audioContext.resume();
                }
            });

            // Handle window resize
            window.addEventListener('resize', this.handleResize.bind(this));
        } catch (error) {
            console.error('Error setting up audio visualizer:', error);
        }
    }

    private handleResize(): void {
        if (this.canvas && this.audioElement) {
            this.canvas.width = this.audioElement.clientWidth || 300;
        }
    }

    private visualize(): void {
        if (!this.analyser || !this.canvasContext || !this.canvas) {
            return;
        }

        this.analyser.getByteFrequencyData(this.dataArray);

        this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);

        const barWidth = (this.canvas.width / this.dataArray.length) * 2.5;
        let barHeight;
        let x = 0;

        for (let i = 0; i < this.dataArray.length; i++) {
            barHeight = this.dataArray[i] / 2;

            // Create gradient
            const gradient = this.canvasContext.createLinearGradient(0, 0, 0, this.canvas.height);
            gradient.addColorStop(0, '#efcb08'); // Yellow at top
            gradient.addColorStop(1, '#f15428'); // Orange at bottom

            this.canvasContext.fillStyle = gradient;
            this.canvasContext.fillRect(x, this.canvas.height - barHeight, barWidth, barHeight);

            x += barWidth + 1;
        }

        this.animationId = requestAnimationFrame(this.visualize.bind(this));
    }

    ngOnDestroy(): void {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }

        if (this.audioContext) {
            try {
                this.audioContext.close();
            } catch (e) {
                console.error('Error closing audio context:', e);
            }
        }

        window.removeEventListener('resize', this.handleResize.bind(this));

        if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
        }
    }
}